<template>
    <div>
        <!-- Edit Custom Panel Modal -->
        <base-modal
            id="edit-custom-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit {{ panelTypeLabel(panel.panel_type) }} Content </template>
            <template>
                <div>
                    <div class="pb-2">
                        <base-form
                            v-model="panel"
                            :fields="editPanelFormFields"
                            :inline-style="true"
                            ref="editPanelForm"
                        ></base-form>
                        <div class="row">
                            <div class="col-12 col-sm-9 offset-sm-3">
                                <div
                                    v-show="panel.show_video_first"
                                    class="pt-1"
                                    :class="{ 'pt-2': panel.preContentVideoElements }"
                                >
                                    <rich-content-editor
                                        v-model="panel.preContentVideoElements"
                                        :allowed-element-types="['vimeo', 'youtube', 'video']"
                                        :allowed-video-fields="[
                                            'autoplay_video',
                                            'play_video_before_continue',
                                            'hide_video_controls',
                                            'display_full_screen',
                                        ]"
                                        :default-checked-options="[
                                            'display_full_screen',
                                            'autoplay_video',
                                        ]"
                                        :show-info-footers="false"
                                        :max-items="1"
                                        ref="choiceRichContentEditor"
                                    ></rich-content-editor>
                                </div>
                            </div>
                        </div>
                        <base-form
                            v-model="panel"
                            :fields="customPanelFormFields"
                            :inline-style="true"
                            ref="customPanelForm"
                        ></base-form>
                    </div>
                    <rich-content-editor
                        :class="{ 'pt-3': panel.elements.length == 0 }"
                        v-model="panel.elements"
                        ref="richContentEditor"
                    ></rich-content-editor>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="editLocked || modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Choice-Like Panel Modal -->
        <base-modal
            id="edit-multichoice-panel-modal"
            :loading="modalLoading || choiceModalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit {{ panelTypeLabel(panel.panel_type) }} </template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <div class="row">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <div
                            v-show="panel.show_video_first"
                            class="pt-1"
                            :class="{ 'pt-2': panel.preContentVideoElements }"
                        >
                            <rich-content-editor
                                v-model="panel.preContentVideoElements"
                                :allowed-element-types="['vimeo', 'youtube', 'video']"
                                :allowed-video-fields="[
                                    'autoplay_video',
                                    'play_video_before_continue',
                                    'hide_video_controls',
                                    'display_full_screen',
                                ]"
                                :default-checked-options="[
                                    'display_full_screen',
                                    'autoplay_video',
                                    'hide_video_controls',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                ref="choiceRichContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                </div>

                <div class="pb-3">
                    <!-- Panel-Specific Config Options -->
                    <div v-show="panel.panel_type == 'multichoice'" class="mb-1">
                        <base-form
                            v-model="panel"
                            :fields="branchingPanelFormFields"
                            :inline-style="true"
                            :condensed-style="true"
                        ></base-form>
                    </div>
                    <div v-show="viewingChoicePanel" class="collapse" ref="extraChoiceFields">
                        <div v-show="viewingSelectionPanel" class="mb-1">
                            <base-form
                                v-model="panel"
                                :fields="selectionPanelFormFields"
                                :inline-style="true"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                    </div>
                    <div v-show="panel.panel_type == 'step_order'" class="collapse" ref="extraStepFields">
                        <base-form
                            v-model="panel"
                            :fields="stepOrderPanelFormFields"
                            :inline-style="true"
                            :condensed-style="true"
                        ></base-form>
                    </div>
                    <div v-show="panel.panel_type == 'categories'" class="collapse" ref="extraCategoriesFields">
                        <base-form
                            v-model="panel"
                            :fields="categoriesPanelFormFieldsExtra"
                            :condensed-style="true"
                        ></base-form>
                    </div>
                    <div v-show="viewingPanelWithExtraSettings">
                        <div class="row mb-1">
                            <div class="col-12 col-sm-9 offset-sm-3">
                                <button
                                    @click="toggleExtraSettingsDisplay()"
                                    class="btn btn-gray badge badge-gray"
                                    aria-label="Toggle additional settings display"
                                >
                                    <span v-show="allChoiceSettingsDisplayed">
                                        Less Settings
                                        <span class="icon icon-xs icon-glyphicons-basic-chevron-up align-middle"></span>
                                    </span>
                                    <span v-show="!allChoiceSettingsDisplayed">
                                        More Settings
                                        <span
                                            class="icon icon-xs icon-glyphicons-basic-chevron-down align-middle"
                                        ></span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-show="panel.panel_type == 'categories'" class="pb-2 mt-3">
                        <div class="mt-2 mb-4">
                            <p class="mb-2">Layout Style</p>
                            <div class="d-flex">
                                <div
                                    v-for="option in categoriesLayoutOptions"
                                    :key="option.id"
                                    class="flex-grow-0 card h-100 panel-type-card panel-type-card-available rounded-lg mr-3"
                                >
                                    <div
                                        class="card-body panel-type-card-body p-2 px-1 rounded-lg"
                                        :class="{ 'panel-type-card-active': panel.layout_style == option.value }"
                                        @click="panel.layout_style = option.value"
                                    >
                                        <img :src="option.iconPath" :alt="option.alt" height="60" class="mw-100" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <base-form
                                    v-model="panel"
                                    :fields="categoriesPanelFormFields"
                                    :condensed-style="true"
                                ></base-form>
                            </div>
                        </div>
                    </div>
                    <div v-show="panel.panel_type == 'tabs'" class="pb-3 mt-3">
                        <p class="mb-0">Layout Style</p>
                        <p class="small text-secondary">Choose how to display your tabbed-content.</p>
                        <div class="d-flex">
                            <div
                                v-for="option in tabsLayoutOptions"
                                :key="option.id"
                                class="flex-grow-0 card h-100 panel-type-card panel-type-card-available rounded-lg"
                                style="margin-right: 20px"
                            >
                                <div
                                    class="card-body panel-type-card-body rounded-lg tabs-layout-container"
                                    :class="{ 'panel-type-card-active': panel.display_variation == option.value }"
                                    @click="panel.display_variation = option.value"
                                >
                                    <img :src="option.iconPath" :alt="option.alt" height="70" class="mw-100" />
                                    <div class="tiny font-weight-500 text-center text-secondary mb-0 mt-2">
                                        {{ option.name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-show="panel.panel_type == 'categories'" class="mb-1">
                        <hr class="my-4" />
                        <p class="h6 mb-1">Step 1: Create Categories</p>
                        <p class="mb-3 small text-secondary">Add categories for items to be dropped into.</p>
                        <div v-for="(category, index) in panel.choice_categories" :key="index" class="mb-1 d-flex">
                            <input
                                type="text"
                                class="form-control category-name-input mr-1"
                                placeholder="Category name"
                                maxlength="500"
                                v-model="panel.choice_categories[index].name"
                            />
                            <div class="dropright">
                                <a
                                    class="btn btn-light px-1"
                                    role="button"
                                    id="dropdownMenuLink"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    <span class="icon icon-lg icon-glyphicons-basic-more-vertical"></span>
                                </a>
                                <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                    <a class="dropdown-item" @click="shiftListItem(index, -1, panel.choice_categories)">
                                        <span class="icon icon-sm icon-glyphicons-basic-arrow-up"></span>
                                        Move Up</a
                                    >
                                    <a class="dropdown-item" @click="shiftListItem(index, 1, panel.choice_categories)">
                                        <span class="icon icon-sm icon-glyphicons-basic-arrow-down"></span>
                                        Move Down</a
                                    >
                                    <a
                                        class="dropdown-item"
                                        @click="removeListItemByIndex(panel.choice_categories, index)"
                                    >
                                        <span class="icon icon-sm icon-glyphicons-basic-bin"></span>
                                        Remove
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="mt-2">
                            <button class="btn btn-sm btn-light" @click="createChoiceCategory()">
                                <span class="icon icon-glyphicons-basic-plus"></span>
                                Category
                            </button>
                        </div>
                        <hr class="my-4" />
                        <p class="h6 mb-1">Step 2: Create Items</p>
                        <p class="mb-2 small text-secondary">Add items for learners to categorize.</p>
                    </div>
                </div>
                <choice-panel-editor
                    v-model="panel"
                    :choices.sync="panel.choices"
                    :node-options="nodeOptions"
                    ref="choicePanelEditor"
                ></choice-panel-editor>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="saveChoicePanel()"
                    :disabled="modalLoading || choiceModalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Text Response Panel Modal -->
        <base-modal
            id="edit-textarea-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Text Response </template>
            <template>
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <div class="row">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <div v-show="panel.show_video_first" class="pt-1">
                            <rich-content-editor
                                v-model="panel.preContentVideoElements"
                                :allowed-element-types="['vimeo', 'youtube', 'video']"
                                :allowed-video-fields="[
                                    'autoplay_video',
                                    'play_video_before_continue',
                                    'hide_video_controls',
                                    'display_full_screen',
                                ]"
                                :default-checked-options="[
                                    'display_full_screen',
                                    'autoplay_video',
                                    'hide_video_controls',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                ref="choiceRichContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                </div>
                <base-form v-model="panel" :fields="textResponseFormFields"></base-form>
                <div v-if="panel.analyze_text_responses">
                    <div
                        v-for="(textEntity, index) in panel.text_entities"
                        :key="index"
                        class="card bg-light pt-3 pb-3 px-3 mb-3"
                    >
                        <div class="d-flex">
                            <div class="flex-grow-1">
                                <base-form
                                    v-model="panel.text_entities[index]"
                                    :fields="textEntityFormFields"
                                    :inline-style="true"
                                ></base-form>
                                <div class="mb-3 d-flex align-items-center">
                                    <span class="mr-2">Valid Phrase/Keyword Variations</span>
                                    <span class="badge badge-gray">{{ textEntity.keywords.length }}</span>
                                </div>
                                <div class="d-flex align-items-center flex-wrap">
                                    <div
                                        class="flex-grow-0 mr-3 mb-2"
                                        v-for="(keyword, index) in textEntity.keywords"
                                        :key="index"
                                    >
                                        <div class="d-flex align-items-center">
                                            <div class="flex-grow-0" style="margin-right: 2px">
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    placeholder="Phrase or keyword"
                                                    v-model="textEntity.keywords[index].value"
                                                />
                                            </div>
                                            <div class="flex-grow-0">
                                                <button
                                                    class="btn btn-transparent btn-sm p-1"
                                                    @click="removeTextEntityKeyword(textEntity, index)"
                                                >
                                                    <span class="icon icon-glyphicons-basic-times"></span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex-grow-0 mb-2">
                                        <button class="btn btn-sm btn-gray" @click="createKeywordVariation(textEntity)">
                                            <span class="icon icon-glyphicons-basic-plus"></span>
                                            Variation
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow-0 d-flex flex-column align-items-center ml-3">
                                <button
                                    class="btn btn-transparent py-0 px-1 mt-1 mb-2"
                                    @click="removeTextEntity(panel, index)"
                                >
                                    <span class="icon icon-glyphicons-basic-bin"></span>
                                </button>
                                <div class="mb-3">
                                    <div class="btn-group-vertical">
                                        <button
                                            type="button"
                                            class="btn btn-transparent btn-sm py-0 px-1"
                                            @click="shiftListItem(index, -1, panel.text_entities)"
                                        >
                                            <span class="icon icon-glyphicons-basic-arrow-up"></span>
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-transparent btn-sm py-0 px-1"
                                            @click="shiftListItem(index, 1, panel.text_entities)"
                                        >
                                            <span class="icon icon-glyphicons-basic-arrow-down"></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <button class="btn btn-sm btn-light" @click="createTextEntity(panel)">
                            <span class="icon icon-glyphicons-basic-plus"></span>
                            Key Info
                        </button>
                    </div>
                </div>
                <!-- AI Response Analysis Fields -->
                <div class="pt-2">
                    <div class="custom-control custom-switch pb-3">
                        <input
                            v-model="panel.enable_ai_analysis"
                            type="checkbox"
                            class="custom-control-input"
                            :id="_uid + 'ai-analysis'"
                        />
                        <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                            AI Response Analysis
                            <ai-badge :enableTooltip="false"></ai-badge>
                            <div class="small text-secondary mt-1 mb-1">
                                Automatically compare the learner's response to an accepted or expert response.
                            </div>
                        </label>
                    </div>
                    <div v-if="panel.enable_ai_analysis && panel.ai_analysis_config" style="padding-left: 36px">
                        <!-- Expert Response -->
                        <div class="mb-1">
                            <span class="badge badge-gray mr-2 align-middle">Step 1</span>
                            <span class="align-middle"
                                >Enter an expert or accepted response
                                <span class="text-secondary">(optional)</span></span
                            >
                        </div>
                        <p class="small text-secondary mb-3">
                            The AI will compare the learner's response with the text you enter below, and provide
                            feedback on the differences.
                        </p>
                        <textarea
                            v-model="panel.ai_analysis_config.expert_response"
                            class="form-control form-control-sm mb-4"
                            placeholder="Enter an expert or accepted response..."
                            rows="5"
                        ></textarea>
                        <!-- Extra Instructions -->
                        <div class="mb-1">
                            <span class="badge badge-gray align-middle mr-2">Step 2</span>
                            <span class="align-middle"
                                >Add extra instructions for the AI <span class="text-secondary">(optional)</span></span
                            >
                        </div>
                        <div class="small text-secondary mb-1">
                            Enter any additional instructions for the AI to take into consideration while creating
                            customized feedback for the learner.
                        </div>
                        <div class="text-secondary small mb-2">
                            Examples:
                            <ul>
                                <li class="font-italic">"Check for proper grammar and spelling."</li>
                                <li class="font-italic">
                                    "Make sure that the learner doesn't mention the client's medical history."
                                </li>
                            </ul>
                        </div>
                        <textarea
                            v-model="panel.ai_analysis_config.extra_instructions"
                            class="form-control form-control-sm mb-4"
                            placeholder="Add instructions for the AI..."
                            rows="5"
                        ></textarea>
                        <base-form
                            v-model="panel"
                            :fields="textResponseAiScoringFields"
                            :inline-style="true"
                            class="mb-2"
                        ></base-form>
                        <!-- Scoring Rubric -->
                        <div v-show="panel.enable_ai_scoring">
                            <div class="mb-1">
                                <span class="badge badge-gray mr-2 align-middle">Step 3</span>
                                <span class="align-middle">Add a scoring rubric</span>
                            </div>
                            <div class="small text-secondary mb-1">
                                Describe to the AI how the learner's response should be scored.
                                <br />
                            </div>
                            <div class="text-secondary small mb-2">
                                Example:
                                <ul>
                                    <li class="font-italic">
                                        "Award the learner one point each for including each section of the SBAR
                                        report."
                                    </li>
                                </ul>
                            </div>
                            <textarea
                                v-model="panel.ai_analysis_config.scoring_rubric"
                                class="form-control form-control-sm"
                                placeholder="Add a scoring rubric..."
                                rows="5"
                            ></textarea>
                            <div class="d-flex align-items-center mt-3">
                                <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                <div class="flex-grow-0">
                                    <input
                                        type="number"
                                        v-model="panel.max_ai_score"
                                        class="form-control form-control-sm"
                                        style="max-width: 60px"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Notepad Panel Modal -->
        <base-modal
            id="edit-notepad-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Notepad </template>
            <template>
                <div class="text-secondary mb-4 pb-2 mt-1" v-if="caseID">
                    <div class="mt-1 pl-2 text-secondary medium">
                        <span class="icon icon-glyphicons-basic-switch-off mr-1"></span>
                        <span>
                            The notepad toolbar button is currently
                            <router-link :to="experienceSettingsRouteObj">
                                <span v-show="caseObj.global_notepad_enabled">enabled</span>
                                <span v-show="!caseObj.global_notepad_enabled">turned off</span>
                            </router-link>
                            for this scenario.
                        </span>
                    </div>
                </div>
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                    class="mb-3"
                ></base-form>
                <div class="row mb-3">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <div
                            v-show="panel.show_video_first"
                            class="pt-1"
                            :class="{ 'pt-2': panel.preContentVideoElements }"
                        >
                            <rich-content-editor
                                v-model="panel.preContentVideoElements"
                                :allowed-element-types="['vimeo', 'youtube', 'video']"
                                :allowed-video-fields="[
                                    'autoplay_video',
                                    'play_video_before_continue',
                                    'hide_video_controls',
                                    'display_full_screen',
                                ]"
                                :default-checked-options="[
                                    'display_full_screen',
                                    'autoplay_video',
                                    'hide_video_controls',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                ref="choiceRichContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                </div>
                <base-form
                    v-model="panel"
                    :fields="notepadPanelFormFields"
                    :inline-style="true"
                    :condensed-style="true"
                ></base-form>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-lg icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Vitals Panel Modal -->
        <base-modal
            id="edit-vitals-panel-modal"
            size="xl"
            :loading="modalLoading"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Vitals </template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="simplePanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <br />
                <p class="h6 mb-2">Configure which vital signs to display:</p>
                <p class="text-secondary small">Choose a code and a color for each vital sign you want to simulate.</p>

                <!-- Vitals Series Config -->
                <div v-if="panel.vitals">
                    <div class="row mb-4">
                        <div class="col-12">
                            <div v-for="(series, index) in activeVitalsSeries" :key="series.id">
                                <div class="d-flex align-items-center mb-2">
                                    <div class="mr-3">
                                        <rich-text-vitals
                                            placeholder="Vitals Symbol (e.g. HR)"
                                            maxlength="20"
                                            v-model="series.code"
                                        ></rich-text-vitals>
                                    </div>
                                    <div class="mr-3">
                                        <div class="dropdown">
                                            <button
                                                class="btn btn-white px-1"
                                                type="button"
                                                id="vitalsColorDropdown"
                                                data-toggle="dropdown"
                                                aria-haspopup="true"
                                                aria-expanded="false"
                                            >
                                                <div class="d-flex align-items-center">
                                                    <div
                                                        class="rounded vitals-color-preview mr-2"
                                                        :style="{ 'background-color': series.hex_color }"
                                                    ></div>
                                                    <div>
                                                        <span
                                                            class="icon icon-xs icon-glyphicons-basic-chevron-down text-secondary"
                                                        ></span>
                                                    </div>
                                                </div>
                                            </button>
                                            <div
                                                class="dropdown-menu dropdown-menu-right vitals-color-dropdown"
                                                aria-labelledby="vitalsColorDropdown"
                                            >
                                                <a
                                                    v-for="color in vitalsColorOptions"
                                                    :key="color"
                                                    class="dropdown-item"
                                                    @click="setVitalsColor(series, color)"
                                                >
                                                    <div
                                                        class="rounded vitals-color-preview"
                                                        :style="{ 'background-color': color }"
                                                    ></div>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="dropright">
                                        <a
                                            class="btn btn-light btn-sm px-1"
                                            role="button"
                                            id="dropdownMenuLink"
                                            data-toggle="dropdown"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                        >
                                            <span class="icon icon-lg icon-glyphicons-basic-more-vertical"></span>
                                        </a>
                                        <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                            <a class="dropdown-item" @click="shiftVitalsSeriesPosition(index, -1)">
                                                <span class="icon icon-sm icon-glyphicons-basic-arrow-up"></span>
                                                Move Up</a
                                            >
                                            <a class="dropdown-item" @click="shiftVitalsSeriesPosition(index, 1)">
                                                <span class="icon icon-sm icon-glyphicons-basic-arrow-down"></span>
                                                Move Down</a
                                            >
                                            <a class="dropdown-item" @click="removeVitalsSeries(series)">
                                                <span class="icon icon-sm icon-glyphicons-basic-bin"></span>
                                                Remove
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <button class="btn btn-sm btn-light mt-2" @click="createVitalsSeries()">
                                    <span class="icon icon-glyphicons-basic-plus"></span>
                                    New
                                </button>
                            </div>
                        </div>
                    </div>

                    <br />
                    <p class="h6 mb-2">Configure Vitals Data</p>
                    <p class="text-secondary small mb-4">
                        Either choose a high and low boundary for each vitals series, or upload a csv containing a
                        series of data points for each vital sign.
                    </p>

                    <div class="row mb-4">
                        <div class="col-12">
                            <div class="custom-control custom-radio custom-control-inline mb-1">
                                <input
                                    type="radio"
                                    id="vitals-boundaries"
                                    name="vitals-boundaries"
                                    class="custom-control-input"
                                    v-model="panel.vitals.use_uploaded_data"
                                    :value="false"
                                />
                                <label class="custom-control-label" for="vitals-boundaries">Set Boundaries</label>
                            </div>
                            <div class="custom-control custom-radio custom-control-inline mb-2">
                                <input
                                    type="radio"
                                    id="vitals-upload"
                                    name="vitals-upload"
                                    class="custom-control-input"
                                    v-model="panel.vitals.use_uploaded_data"
                                    :value="true"
                                />
                                <label class="custom-control-label" for="vitals-upload"> Upload CSV</label>
                            </div>
                        </div>
                    </div>

                    <div v-if="!panel.vitals.use_uploaded_data">
                        <div v-for="series in activeVitalsSeries" :key="series.id" class="card bg-light p-2 px-3 mb-2">
                            <div class="d-flex align-items-start mb-1">
                                <div class="flex-grow-0">
                                    <div class="mr-2 pt-1 vitals-code vitals-boundary-code" v-html="series.code"></div>
                                </div>
                                <div class="flex-grow-1">
                                    <div class="d-flex align-items-center mb-1">
                                        <div class="vitals-glyph-spacer flex-grow-0 flex-shrink-0"></div>
                                        <div class="mr-2">
                                            <input
                                                type="text"
                                                class="form-control form-control-sm vitals-boundary-input"
                                                placeholder="Lowest"
                                                v-model="series.low_boundary"
                                            />
                                        </div>
                                        <div class="mx-1">to</div>
                                        <div class="mx-2">
                                            <input
                                                type="text"
                                                class="form-control form-control-sm vitals-boundary-input"
                                                placeholder="Highest"
                                                v-model="series.high_boundary"
                                            />
                                        </div>
                                        <div class="ml-3">
                                            <div class="custom-control custom-checkbox">
                                                <input
                                                    v-model="series.show_decimals"
                                                    type="checkbox"
                                                    class="custom-control-input"
                                                    :id="'show-decimals-' + series.id"
                                                />
                                                <label class="custom-control-label" :for="'show-decimals-' + series.id"
                                                    >Show Decimals</label
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mb-1">
                                        <div v-for="(trigger, index) in series.triggers" :key="index" class="mt-1">
                                            <div class="d-flex flex-wrap flex-lg-nowrap align-items-center">
                                                <div class="vitals-glyph-spacer flex-grow-0 flex-shrink-0">
                                                    <span
                                                        class="icon icon-glyphicons-basic-flash text-secondary icon-xs"
                                                    ></span>
                                                </div>
                                                <div class="flex-grow-0 flex-shrink-0 mr-1">
                                                    <select
                                                        v-model="trigger.change_type"
                                                        class="form-control form-control-sm"
                                                    >
                                                        <option value="replace_with">Change limits to</option>
                                                        <option value="increase_by">Change limits by</option>
                                                    </select>
                                                </div>
                                                <!-- Change-To Settings -->
                                                <div
                                                    v-show="trigger.change_type == 'replace_with'"
                                                    class="flex-grow-0 mx-2"
                                                >
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0">
                                                            <input
                                                                type="text"
                                                                placeholder="Lowest"
                                                                class="form-control form-control-sm vitals-boundary-input"
                                                                v-model="trigger.low_boundary"
                                                            />
                                                        </div>
                                                        <div class="flex-grow-0 mx-2">to</div>
                                                        <div class="flex-grow-0">
                                                            <input
                                                                type="text"
                                                                placeholder="Highest"
                                                                class="form-control form-control-sm vitals-boundary-input"
                                                                v-model="trigger.high_boundary"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <!-- Change-By Settings -->
                                                <div
                                                    v-show="trigger.change_type == 'increase_by'"
                                                    class="flex-grow-0 mx-2"
                                                >
                                                    <input
                                                        type="text"
                                                        placeholder="Value"
                                                        class="form-control form-control-sm vitals-boundary-input"
                                                        v-model="trigger.increase_value"
                                                    />
                                                </div>
                                                <div class="flex-grow-0 mr-2">
                                                    <select
                                                        v-model="trigger.trigger_type"
                                                        class="form-control form-control-sm"
                                                    >
                                                        <option value="choice">when</option>
                                                        <option value="time_delay">after delay of</option>
                                                    </select>
                                                </div>
                                                <div v-show="trigger.trigger_type == 'choice'" class="flex-grow-0 mr-2">
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0 mr-2">
                                                            <select
                                                                class="form-control form-control-sm"
                                                                v-model="trigger.choice_id"
                                                            >
                                                                <option
                                                                    v-for="choice in panelChoiceInquiries"
                                                                    :key="choice.id"
                                                                    :value="choice.id"
                                                                >
                                                                    {{ choice.text }}
                                                                </option>
                                                            </select>
                                                        </div>
                                                        <div class="flex-grow-0 flex-shrink-0 small">is selected</div>
                                                    </div>
                                                </div>
                                                <div v-show="trigger.trigger_type == 'time_delay'">
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0 mr-2">
                                                            <input
                                                                type="number"
                                                                placeholder="Value"
                                                                class="form-control form-control-sm vitals-boundary-input"
                                                                v-model="trigger.time_delay_seconds"
                                                            />
                                                        </div>
                                                        <div class="flex-grow-0 small">seconds</div>
                                                    </div>
                                                </div>
                                                <div class="ml-2">
                                                    <div class="my-1">
                                                        <span
                                                            class="icon icon-glyphicons-basic-times icon-btn icon-sm text-secondary"
                                                            @click="removeTrigger(series, index)"
                                                        ></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="small d-flex">
                                        <div class="vitals-glyph-spacer"></div>
                                        <button class="btn btn-gray badge" @click="addTrigger(series)">
                                            + Trigger
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="panel.vitals.use_uploaded_data">
                        <p class="mt-2 mb-0">Uploading a CSV</p>
                        <ul class="mb-4">
                            <li>If you're starting from scratch, download the template file.</li>
                            <li>
                                Each data series will be matched to its vital sign based on the
                                <strong>order</strong> it appears in the CSV (not by column headers).
                            </li>
                            <li>Each row of data represents 800ms of the vitals display.</li>
                            <li>All columns must contain the same number of rows.</li>
                        </ul>

                        <!-- Download Template .csv File -->
                        <div class="mb-1">
                            <form method="GET" :action="'/api/vitals/template/'">
                                <input
                                    type="text"
                                    style="display: none"
                                    name="seriesCodes"
                                    :value="vitalsSeriesCodesSerialized"
                                />
                                <button class="btn btn-light btn-sm" type="submit">
                                    <span class="icon icon-lg icon-glyphicons-basic-cloud-download mr-2"></span>Download
                                    Template File
                                </button>
                            </form>
                        </div>
                        <br />

                        <!-- Uploaded Vitals -->
                        <div v-if="panel.vitals">
                            <file-upload
                                v-model="panel.vitals.csv_file"
                                :replaceable="true"
                                placeholder="Drop a CSV file here"
                                accept=".csv"
                            ></file-upload>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="saveVitalsPanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Linked Response Panel Modal -->
        <base-modal
            id="edit-linked-response-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Displayed Response </template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <!-- Linked Response Panel Dropdown -->
                <p class="mt-4 pt-3 mb-1">Select an inquiry from the list below.</p>
                <p class="small text-secondary mb-3">
                    The learner's response to this inquiry will be displayed when visiting this screen.
                </p>
                <v-select
                    v-model="panel.linked_response_panel"
                    :multiple="false"
                    :options="linkablePanels"
                    :close-on-select="true"
                    label="display_name"
                    class="mw-100 mb-3"
                    placeholder="Select an inquiry"
                    :reduce="(option) => option.id"
                >
                    <template slot="option" slot-scope="option">
                        <span class="mr-2 align-middle">{{ option.reporting_label || option.header }}</span>
                        <span class="badge badge-pill badge-light align-middle font-weight-500 mr-2">
                            <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                            <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                        </span>
                        <span class="text-secondary small align-middle font-weight-500">
                            <span class="align-middle mr-1">Screen {{ option.node.number }}</span>
                        </span>
                    </template>
                    <template v-slot:selected-option="option">
                        <div v-if="option.node">
                            <span class="mr-2 align-middle">{{ option.reporting_label || option.header }}</span>
                            <span class="badge badge-pill badge-light align-middle font-weight-500 mr-2">
                                <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                            </span>
                            <span class="text-secondary small align-middle font-weight-500">
                                <span class="align-middle mr-1">Screen {{ option.node.number }}</span>
                            </span>
                        </div>
                    </template>
                    <template slot="no-options">
                        <span class="text-secondary">No inquiries to display.</span>
                    </template>
                </v-select>
            </template>
            <template slot="footer">
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked || !panel.linked_response_panel"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Results Panel Modal -->
        <base-modal
            id="edit-results-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Learner Results </template>
            <template>
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <base-form v-model="panel" :fields="scorePanelFormFields" :inline-style="true"></base-form>
                <div class="col-12 col-sm-9 offset-sm-3" v-if="freeTierActive">
                    <p class="text-secondary small">
                        <span class="icon icon-glyphicons-basic-lightbulb"></span>
                        <span>
                            Note: Learner Results PDF downloads are only available on paid plans, or on scenarios
                            published to the community. To start a paid plan, reach out to us at
                            <a target="_blank" href="mailto:support@affinitylearning.ca?subject=User Capacity Increase"
                                >support@affinitylearning.ca</a
                            >.
                        </span>
                    </p>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Basic Panel Modal -->
        <base-modal
            id="edit-basic-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit {{ panelTypeLabel(panel.panel_type) }} </template>
            <template>
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit VR Panel Modal -->
        <base-modal
            id="edit-vr-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title">Edit 360&deg; Video</template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <rich-content-editor
                    v-model="panel.elements"
                    :allowed-element-types="['vr']"
                    :allowed-video-fields="[
                        'loop_media',
                        'play_video_before_continue',
                        'continue_after_video_finished',
                    ]"
                    :show-info-footers="false"
                    :max-items="1"
                    ref="vrPanelContentEditor"
                    class="mt-5"
                ></rich-content-editor>
                <base-form v-model="panel" :fields="threeDVideoFormFields" class="pl-5 mt-3"></base-form>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Image Hotspots Panel Modal -->
        <base-modal
            id="edit-hotspots-panel-modal"
            size="large"
            :loading="modalLoading"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title">Edit Image Hotspots</template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <br />
                <p class="h6 mb-2">Step 1: Add an Image</p>
                <p class="text-secondary small mb-4">Add an image to place hotspots onto.</p>
                <!-- Image File -->
                <div class="mb-4">
                    <rich-content-editor
                        v-model="panel.elements"
                        :allowed-element-types="['image']"
                        :allowed-image-fields="['accessibility_alt_text']"
                        :show-info-footers="false"
                        :max-items="1"
                        :max-image-bytes="40000000"
                        :warning-image-bytes="30000000"
                    ></rich-content-editor>
                    <hr />
                    <p class="h6 mb-2 mt-4">Step 2: Configure</p>
                    <p class="text-secondary small mb-2">Configure your hotspots activity.</p>
                    <base-form
                        class="mt-3"
                        ref="imageHotspotsForm"
                        v-model="panel"
                        :fields="imageHotspotsFormFields"
                        :inline-style="false"
                        :condensed-style="false"
                    ></base-form>
                </div>
                <div v-if="panel.id && panelHasElements && panel.elements[0].stored_file">
                    <hr />
                    <p class="h6 mb-2">Step 3: Add Hotspots</p>
                    <p class="text-secondary small mb-3">Add hotspots to your image using the hotspots editor.</p>
                    <p class="mb-2">
                        <button class="btn btn-link px-0" @click="navToHotspotsEditor()">
                            <span class="icon icon-glyphicons-basic-square-edit icon-mr"></span>
                            <span v-if="!editLocked && panelIsEdited">Save & View Hotspots Editor</span>
                            <span v-else>View Hotspots Editor</span>
                        </button>
                    </p>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="saveHotspotsPanel(true)"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Custom Form Panel Modal -->
        <base-modal
            id="edit-custom-form-panel-modal"
            size="large"
            :loading="modalLoading"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title">Edit Custom Form</template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <div class="row">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <div
                            v-show="panel.show_video_first"
                            class="pt-1"
                            :class="{ 'pt-2': panel.preContentVideoElements }"
                        >
                            <rich-content-editor
                                v-model="panel.preContentVideoElements"
                                :allowed-element-types="['vimeo', 'youtube', 'video']"
                                :allowed-video-fields="[
                                    'autoplay_video',
                                    'play_video_before_continue',
                                    'hide_video_controls',
                                    'display_full_screen',
                                ]"
                                :default-checked-options="[
                                    'display_full_screen',
                                    'autoplay_video',
                                    'hide_video_controls',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                ref="choiceRichContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                </div>
                <div>
                    <base-form
                        v-model="panel"
                        :fields="customFormPanelFields"
                        :condensed-style="true"
                        :inline-style="true"
                    ></base-form>
                </div>
                <div class="collapse" ref="extraFormFields">
                    <base-form
                        v-model="panel"
                        :fields="customFormPanelFieldsExtra"
                        :condensed-style="true"
                        :inline-style="true"
                    ></base-form>
                </div>
                <div class="row">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <button
                            @click="toggleExtraSettingsDisplay()"
                            class="btn btn-gray badge badge-gray"
                            aria-label="Toggle additional settings display"
                        >
                            <span v-show="allChoiceSettingsDisplayed">
                                Less Settings
                                <span class="icon icon-xs icon-glyphicons-basic-chevron-up align-middle"></span>
                            </span>
                            <span v-show="!allChoiceSettingsDisplayed">
                                More Settings
                                <span class="icon icon-xs icon-glyphicons-basic-chevron-down align-middle"></span>
                            </span>
                        </button>
                    </div>
                </div>
                <div class="row mt-4 mb-2">
                    <div class="col-12 col-md-9 offset-md-3">
                        <router-link v-if="customFormEditorRoute" :to="customFormEditorRoute" data-dismiss="modal">
                            <span class="icon icon-glyphicons-basic-square-edit icon-mr"></span>
                            <span>Open Custom Form Builder</span>
                        </router-link>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Voice Panel Modal -->
        <base-modal
            id="edit-voice-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit {{ panelTypeLabel(panel.panel_type) }} </template>
            <template>
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                    class="mb-3"
                ></base-form>
                <div class="row mb-3">
                    <div class="col-12 col-sm-9 offset-sm-3">
                        <div
                            v-show="panel.show_video_first"
                            class="pt-1"
                            :class="{ 'pt-2': panel.preContentVideoElements }"
                        >
                            <rich-content-editor
                                v-model="panel.preContentVideoElements"
                                :allowed-element-types="['vimeo', 'youtube', 'video']"
                                :allowed-video-fields="[
                                    'autoplay_video',
                                    'play_video_before_continue',
                                    'hide_video_controls',
                                    'display_full_screen',
                                ]"
                                :default-checked-options="[
                                    'display_full_screen',
                                    'autoplay_video',
                                    'hide_video_controls',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                ref="choiceRichContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                </div>
                <base-form
                    v-model="panel"
                    :fields="voiceRecordingPanelFields"
                    :inline-style="true"
                    ref="voiceRecordingPanelForm"
                ></base-form>
                <div class="pt-0">
                    <div class="custom-control custom-switch pb-3">
                        <input
                            v-model="panel.transcribe_audio"
                            type="checkbox"
                            class="custom-control-input"
                            :id="_uid + 'ai-transcription'"
                        />
                        <label class="custom-control-label" :for="_uid + 'ai-transcription'">
                            <span class="align-middle mr-2">Transcribe voice to text</span>
                            <ai-badge :enableTooltip="false"></ai-badge>
                            <div class="small text-secondary mt-1 mb-1">
                                Transcribe the learner's voice recordings to text
                            </div>
                        </label>
                    </div>
                    <div class="custom-control custom-switch pb-3">
                        <input
                            v-model="panel.enable_ai_analysis"
                            type="checkbox"
                            class="custom-control-input"
                            :id="_uid + 'ai-analysis'"
                        />
                        <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                            <span class="align-middle mr-2">AI Response Analysis</span>
                            <ai-badge :enableTooltip="false"></ai-badge>
                            <div class="small text-secondary mt-1 mb-1">
                                Automatically compare the learner's response to an accepted or expert response.
                            </div>
                        </label>
                    </div>
                    <div v-if="panel.enable_ai_analysis && panel.ai_analysis_config" style="padding-left: 36px">
                        <!-- Expert Response -->
                        <div class="mb-1">
                            <span class="badge badge-gray mr-2 align-middle">Step 1</span>
                            <span class="align-middle"
                                >Enter an expert or accepted response
                                <span class="text-secondary">(optional)</span></span
                            >
                        </div>
                        <p class="small text-secondary mb-3">
                            The AI will compare the learner's response with the text you enter below, and provide
                            feedback on the differences.
                        </p>
                        <textarea
                            v-model="panel.ai_analysis_config.expert_response"
                            class="form-control form-control-sm mb-4"
                            placeholder="Enter an expert or accepted response..."
                            rows="5"
                        ></textarea>
                        <!-- Extra Instructions -->
                        <div class="mb-1">
                            <span class="badge badge-gray align-middle mr-2">Step 2</span>
                            <span class="align-middle"
                                >Add extra instructions for the AI <span class="text-secondary">(optional)</span></span
                            >
                        </div>
                        <div class="small text-secondary mb-1">
                            Enter any additional instructions for the AI to take into consideration while creating
                            customized feedback for the learner.
                        </div>
                        <div class="text-secondary small mb-2">
                            Examples:
                            <ul>
                                <li class="font-italic">"Check for proper grammar and spelling."</li>
                                <li class="font-italic">
                                    "Make sure that the learner doesn't mention the client's medical history."
                                </li>
                            </ul>
                        </div>
                        <textarea
                            v-model="panel.ai_analysis_config.extra_instructions"
                            class="form-control form-control-sm mb-4"
                            placeholder="Add instructions for the AI..."
                            rows="5"
                        ></textarea>
                        <base-form
                            v-model="panel"
                            :fields="textResponseAiScoringFields"
                            :inline-style="true"
                            class="mb-2"
                        ></base-form>
                        <!-- Scoring Rubric -->
                        <div v-show="panel.enable_ai_scoring">
                            <div class="mb-1">
                                <span class="badge badge-gray mr-2 align-middle">Step 3</span>
                                <span class="align-middle">Add a scoring rubric</span>
                            </div>
                            <div class="small text-secondary mb-1">
                                Describe to the AI how the learner's response should be scored.
                                <br />
                            </div>
                            <div class="text-secondary small mb-2">
                                Example:
                                <ul>
                                    <li class="font-italic">
                                        "Award the learner one point each for including each section of the SBAR
                                        report."
                                    </li>
                                </ul>
                            </div>
                            <textarea
                                v-model="panel.ai_analysis_config.scoring_rubric"
                                class="form-control form-control-sm"
                                placeholder="Add a scoring rubric..."
                                rows="5"
                            ></textarea>
                            <div class="d-flex align-items-center mt-3">
                                <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                <div class="flex-grow-0">
                                    <input
                                        type="number"
                                        v-model="panel.max_ai_score"
                                        class="form-control form-control-sm"
                                        style="max-width: 60px"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Discovery Panel Modal -->
        <base-modal
            id="discovery-panel-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title"> Edit Discovery Display </template>
            <template>
                <!-- Basic Panel Info -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <base-form v-model="panel" :fields="discoveryPanelFormFields" :inline-style="true"></base-form>
                <!-- Panel Selector -->
                <div class="col-12 col-sm-9 offset-sm-3">
                    <div class="mb-2">Show discovered responses for:</div>
                    <v-select
                        v-model="panel.linked_response_panel"
                        :multiple="false"
                        :options="discoveryCompatiblePanels"
                        :close-on-select="true"
                        label="header"
                        class="mw-100 mb-3"
                        placeholder="Select an inquiry"
                        :reduce="(option) => option.id"
                    >
                        <template slot="option" slot-scope="option">
                            <span class="align-middle">{{ option.reporting_label || option.header }}</span>
                            <span class="badge badge-pill badge-light align-middle font-weight-500 ml-1">
                                <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                            </span>
                        </template>
                        <template v-slot:selected-option="option">
                            <span class="align-middle">{{ option.reporting_label || option.header }}</span>
                            <span class="badge badge-pill badge-light align-middle font-weight-500 ml-1">
                                <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                            </span>
                        </template>
                        <template slot="no-options">
                            <span class="text-secondary">No inquiries to display.</span>
                        </template>
                    </v-select>
                </div>
            </template>
            <template slot="footer">
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Chat Panel Editor (Legacy) -->
        <chat-panel-editor
            ref="chatPanelEditor"
            :saved-panel="panel"
            :loading="modalLoading"
            :edit-panel-form-fields="editPanelFormFields"
            v-on:save="savePanel($event)"
            v-on:open-delete="openDeletePanel()"
            v-on:clone="openClone()"
        ></chat-panel-editor>

        <!-- Chat Panel V2 Editor -->
        <base-modal
            id="edit-chat-v2-modal"
            :loading="modalLoading"
            size="large"
            :centered="true"
            data-panel-edit-modal="true"
        >
            <template slot="title">
                <span class="align-middle">Edit {{ panelTypeLabel(panel.panel_type) }}</span>
            </template>
            <template>
                <!-- Basic Panel Form -->
                <base-form
                    v-model="panel"
                    :fields="editPanelFormFields"
                    :inline-style="true"
                    ref="editPanelForm"
                ></base-form>
                <!-- Panel-Specific Form -->
                <base-form
                    v-model="panel.ai_analysis_config"
                    :fields="customGPTFormFields"
                    :inline-style="true"
                    ref="customGPTForm"
                ></base-form>
                <!-- Persona -->
                <div class="mb-4 mt-5">
                    <div class="mb-2">
                        <span class="badge badge-gray mr-2 align-middle"
                            ><span class="icon icon-glyphicons-basic-user align-middle"></span> Persona &
                            Knowledge</span
                        >
                        <span class="align-middle">Who do you want to simulate? What do they know?</span>
                    </div>
                    <p class="small text-secondary mb-3">
                        Describe the person you want to simulate. Include <strong>all details</strong> the GPT should
                        know and be able to respond to.
                    </p>
                    <textarea
                        v-model="panel.ai_analysis_config.persona_details"
                        class="form-control form-control-sm mb-1"
                        placeholder="You are..."
                        rows="10"
                    ></textarea>
                    <div class="mb-3 d-flex align-items-center">
                        <button
                            class="btn btn-sm btn-link p-1 small mr-1"
                            :disabled="panel.isGeneratingPersona"
                            role="button"
                            @click="generateChatPersona(panel)"
                        >
                            <span class="align-middle mr-xs" :class="{ 'text-light-wave': panel.isGeneratingPersona }">
                                Generate an example
                            </span>
                            <img
                                v-show="!panel.isGeneratingPersona"
                                src="/static/general/img/icons/shines.svg"
                                height="11"
                                class="align-middle"
                            />
                        </button>
                        <div v-show="panel.isGeneratingPersona">
                            <div class="custom-spinner custom-spinner-sm" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Extra Instructions -->
                <div class="mt-2 mb-4 pb-2">
                    <div class="mb-2">
                        <span
                            v-if="panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions"
                            class="badge badge-gray"
                        >
                            <span class="icon icon-glyphicons-basic-circle-info mr-1"></span>
                            <span>Add Extra Instructions</span>
                        </span>
                        <a
                            v-else
                            class="btn btn-sm badge badge-gray"
                            @click="$set(panel, 'show_extra_instructions', true)"
                        >
                            <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                            <span>Add Extra Instructions</span>
                        </a>
                    </div>
                    <div v-if="panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions">
                        <p class="small text-secondary mb-3">
                            Add any additional instructions to guide the way the GPT responds to learners.
                        </p>
                        <div class="mt-3">
                            <textarea
                                v-model="panel.ai_analysis_config.extra_instructions"
                                class="form-control form-control-sm mb-1"
                                placeholder="E.g. If the learner asks the same question twice, get upset/impatient"
                                rows="6"
                            ></textarea>
                        </div>
                    </div>
                </div>
                <!-- Key Interactions -->
                <div class="mb-4">
                    <div class="mb-2">
                        <span class="badge badge-gray mr-2 align-middle"
                            ><span class="icon icon-glyphicons-basic-chat-message align-middle"></span> Key
                            Interactions</span
                        >
                        <span class="align-middle"
                            >Key Interactions
                            <span class="text-secondary">(Optional)</span>
                        </span>
                    </div>
                    <p class="small text-secondary mb-3">What important things should the learner ask or say?</p>

                    <div v-for="(intent, index) in panel.chat_intents" :key="index">
                        <div v-show="!intent.is_deleted" class="rounded bg-offwhite p-3 my-4">
                            <div class="row pb-3">
                                <div class="col-4">
                                    <div class="pb-2 small font-weight-500">Label</div>
                                    <input
                                        v-model="intent.message"
                                        class="form-control form-control-sm"
                                        type="text"
                                        placeholder="Label (e.g. Age)"
                                        ref="intentMessage"
                                        maxlength="30"
                                    />
                                </div>
                                <div class="col-4">
                                    <div class="pb-2 small font-weight-500">Correct/Incorrect</div>
                                    <select v-model="intent.correct" class="form-control form-control-sm">
                                        <option :value="true">Correct</option>
                                        <option :value="false">Incorrect</option>
                                        <option :value="null">Neutral</option>
                                    </select>
                                </div>
                                <div class="col-4">
                                    <div class="pb-2 small font-weight-500">Score</div>
                                    <input
                                        v-model="intent.score"
                                        class="form-control form-control-sm"
                                        type="text"
                                        placeholder="Score"
                                    />
                                </div>
                            </div>
                            <div class="pb-2">
                                <div class="pb-2 small font-weight-500">
                                    Briefly describe what the learner should ask or say
                                </div>
                                <textarea
                                    v-model="intent.ai_description"
                                    class="form-control form-control-sm"
                                    rows="2"
                                    placeholder="E.g. patient age, pain level..."
                                ></textarea>
                            </div>
                            <div class="pb-2 pt-1" v-show="intent.feedback_message || intent.showFeedbackInput">
                                <div class="pb-2 small font-weight-500">
                                    Add a feedback message to display if this interaction is activated
                                </div>
                                <textarea
                                    v-model="intent.feedback_message"
                                    class="form-control form-control-sm"
                                    rows="2"
                                    ref="intentFeedbackMessageInput"
                                ></textarea>
                            </div>
                            <div class="d-flex align-items-center mt-2">
                                <div class="flex-grow-1">
                                    <button
                                        v-show="!(intent.feedback_message || intent.showFeedbackInput)"
                                        class="btn btn-sm btn-link p-1 tiny mr-1"
                                        @click="showIntentFeedbackField(intent, index)"
                                    >
                                        + Feedback Message
                                    </button>
                                </div>
                                <div class="flex-grow-0">
                                    <button
                                        @click="shiftListItem(index, -1, panel.chat_intents)"
                                        class="btn btn-transparent p-1"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-arrow-up"></span>
                                    </button>
                                    <button
                                        @click="shiftListItem(index, 1, panel.chat_intents)"
                                        class="btn btn-transparent p-1"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-arrow-down"></span>
                                    </button>
                                    <button @click="removeIntent(intent)" class="btn btn-transparent p-1">
                                        <span class="icon icon-100 icon-glyphicons-basic-bin"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        @click="createIntent(panel.chat_intents.length + 1)"
                        class="btn btn-sm btn-link p-1"
                        role="button"
                    >
                        <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                        <span>Key Interaction</span>
                    </button>
                </div>
            </template>
            <template slot="footer">
                <button @click="openClone()" type="button" class="btn btn-light">
                    <span class="icon-glyphicons-basic-squares mr-1"></span>
                    <span>Clone</span>
                </button>
                <button @click="openDeletePanel()" :disabled="editLocked" type="button" class="btn btn-light">
                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                    <span>Delete</span>
                </button>
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="savePanel()"
                    :disabled="modalLoading || editLocked || !panel.ai_analysis_config.persona_details"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Clone Panel Modal -->
        <base-modal id="clone-panel-modal" :loading="modalLoading" size="medium" :centered="true">
            <template slot="title"> Clone Content </template>
            <template>
                <p class="mb-2 pb-1">Clone content into:</p>
                <div class="mb-2">
                    <div class="custom-control custom-radio mb-2">
                        <input
                            v-model="clonePanelInsideNode"
                            :value="true"
                            type="radio"
                            :id="panel.id + 'clone-panel-internal'"
                            class="custom-control-input"
                            :disabled="editLocked"
                        />
                        <label class="custom-control-label" :for="panel.id + 'clone-panel-internal'">This screen</label>
                        <p class="mb-0 text-secondary small">Clone content into the screen you're currently viewing.</p>
                    </div>
                    <div class="custom-control custom-radio">
                        <input
                            v-model="clonePanelInsideNode"
                            :value="false"
                            type="radio"
                            :id="panel.id + 'clone-panel-external'"
                            class="custom-control-input"
                        />
                        <label class="custom-control-label" :for="panel.id + 'clone-panel-external'"
                            >A different screen</label
                        >
                        <p class="mb-0 text-secondary small">Clone content into a different screen or scenario.</p>
                    </div>
                </div>
                <div v-show="!clonePanelInsideNode" class="mt-3 ml-sm-1">
                    <div class="row mb-3">
                        <div class="col-12">
                            <div class="col-12 col-form-label">Scenario:</div>
                            <div class="col-12">
                                <library-item-selector
                                    v-model="cloneForm.libraryItem"
                                    :item-types="['owned_case']"
                                    placeholder="Select a scenario..."
                                    emptyText="No scenarios found matching your search."
                                    ref="itemSelector"
                                ></library-item-selector>
                            </div>
                        </div>
                    </div>
                    <div>
                        <base-form
                            v-model="cloneForm"
                            :fields="clonePanelFields"
                            :inline-style="false"
                            ref="clonePanelForm"
                        ></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="clonePanel()"
                    :disabled="modalLoading || !clonePanelFormValid"
                    class="btn btn-primary"
                    type="button"
                >
                    Clone
                </button>
            </template>
        </base-modal>

        <!-- Delete Panel Modal -->
        <base-modal id="delete-panel-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Delete - {{ panelTypeLabel(panel.panel_type) }} </template>
            <template>
                <p>Remove this content from the screen?</p>
            </template>
            <template slot="footer">
                <button @click="cancelDeletePanel()" class="btn btn-secondary" type="button">Cancel</button>
                <button @click="deletePanel()" class="btn btn-danger" type="button">Delete</button>
            </template>
        </base-modal>

        <!-- Global Case Styles -->
        <global-style-block v-if="caseObj" :case-settings="caseObj"></global-style-block>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import FileUpload from "@/components/FileUpload"
import RichTextVitals from "@/components/RichTextVitals"
import GlobalStyleBlock from "@/components/GlobalStyleBlock"
import ChoicePanelEditor from "@/components/ChoicePanelEditor"
import ChatPanelEditor from "@/components/ChatPanelEditor"
import RichContentEditor from "@/components/RichContentEditor"
import LibraryItemSelector from "@/components/LibraryItemSelector"
import AiBadge from "@/components/AiBadge"
import { panelTypeIcons, panelTypeLabels } from "@/assets/js/settings.js"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "PanelEditor",
    props: {
        caseObj: {
            type: Object,
            required: false,
            default() {
                return {}
            },
        },
        node: {
            type: Object,
            required: false,
            default() {
                return {
                    panels: [],
                }
            },
        },
        preserveMap: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        BaseForm,
        BaseModal,
        FileUpload,
        GlobalStyleBlock,
        ChoicePanelEditor,
        RichTextVitals,
        ChatPanelEditor,
        RichContentEditor,
        LibraryItemSelector,
        AiBadge,
    },
    data() {
        return {
            panel: {
                id: null,
                vitals: {
                    series: [],
                },
                elements: [],
                choice_categories: [],
                text_entities: [],
                ai_analysis_config: {},
            },
            savedPanel: {},
            modalLoading: false,
            choiceModalLoading: false,
            textResponsePanels: [],
            linkablePanels: [],
            textEntityFormFields,
            vitalsColorOptions: [
                "#f93446",
                "#3fcad0",
                "#37a9fd",
                "#f9c912",
                "#eff1f3",
                "#42d264",
                "#fd7e14",
                "#e53df2",
            ],
            cloneForm: {
                libraryItem: null,
                nodeId: null,
            },
            cloneNodeOptions: [],
            cloneNodeOptionsLoading: false,
            allChoiceSettingsDisplayed: false,
            branchingPanelFormFields,
            scorePanelFormFields,
            notepadPanelFormFields,
            customPanelFormFields,
            discoveryPanelFormFields,
            categoriesLayoutOptions: [
                {
                    value: "vertical",
                    iconPath: "/static/general/img/icons/categories-vertical.svg",
                    alt: "Vertical layout",
                },
                {
                    value: "horizontal",
                    iconPath: "/static/general/img/icons/categories-horizontal.svg",
                    alt: "Horizontal layout",
                },
            ],
            tabsLayoutOptions: [
                {
                    value: "default",
                    iconPath: "/static/general/img/icons/tabs-folder.svg",
                    name: "Folder-style",
                    alt: "Default tabs style",
                },
                {
                    value: "accordion",
                    iconPath: "/static/general/img/icons/tabs-accordion.svg",
                    name: "Accordion-style",
                    alt: "Accordion style",
                },
            ],
            textResponseAiScoringFields: [
                {
                    name: "Enable Scoring",
                    type: "checkbox",
                    key: "enable_ai_scoring",
                    condensedStyle: true,
                },
            ],
            panelIsEdited: false,
            clonePanelInsideNode: true,
        }
    },
    computed: {
        caseID() {
            if (!this.caseObj) {
                return null
            }
            return this.caseObj.id
        },
        nodeID() {
            if (!this.node) {
                return null
            }
            return this.node.id
        },
        currentOrganizationId() {
            return this.$store.getters.currentOrganizationId
        },
        // A flattened list of all the panels contained in the current screen.
        panelsFlat() {
            if (!this.node.columns) {
                return []
            }
            return this.node.columns.reduce((acc, column) => acc.concat(column.panels), [])
        },
        editLocked() {
            return this.$store.getters.caseIsReadOnly
        },
        experienceSettingsRouteObj() {
            return {
                name: "CaseSettings",
                params: { id: this.caseID },
                query: { view: "experience" },
            }
        },
        freeTierActive() {
            return this.$store.getters.freeTierActive
        },
        nodeOptions() {
            let allNodes = [...this.$store.getters.nodeOptions]
            // Remove the node that's currently being viewed.
            let index = allNodes.findIndex((n) => n.id === this.nodeID)
            allNodes.splice(index, 1)
            return allNodes
        },
        cloneCaseId() {
            return this.cloneForm.libraryItem ? this.cloneForm.libraryItem.case.id : null
        },
        panelHasElements() {
            return this.panel.elements && this.panel.elements.length > 0
        },
        textResponsePanelsFlat() {
            return this.textResponsePanels.map((panel) => {
                return {
                    id: panel.id,
                    title: panel.display_name,
                }
            })
        },
        discoveryCompatiblePanels() {
            return this.panelsFlat.filter((p) => {
                return [
                    "multichoice",
                    "multiselect",
                    "singleselect",
                    "step_order",
                    "image_hotspots",
                    "categories",
                    "custom_form",
                    "chat",
                    "chat_v2",
                ].includes(p.panel_type)
            })
        },
        editPanelFormFields() {
            return [
                {
                    name: "Reporting Label",
                    key: "label",
                    required: false,
                    description: "An optional label used to identify this inquiry in reports.",
                    hidden(value) {
                        let inquiryPanelTypes = [
                            "textarea",
                            "multichoice",
                            "multiselect",
                            "singleselect",
                            "step_order",
                            "chat",
                            "chat_v2",
                            "image_hotspots",
                            "categories",
                            "custom_form",
                            "voice_recording",
                        ]
                        return !inquiryPanelTypes.includes(value.panel_type)
                    },
                },
                {
                    name: "Content-Type",
                    key: "panel_type",
                    required: false,
                    type: "select",
                    options: [
                        {
                            value: "multichoice",
                            label: "Buttons",
                        },
                        {
                            value: "multiselect",
                            label: "Multiple Selection",
                        },
                        {
                            value: "singleselect",
                            label: "Multiple Choice",
                        },
                        {
                            value: "tabs",
                            label: "Tabs",
                        },
                    ],
                    hidden(value) {
                        return (
                            !(
                                value.panel_type == "multichoice" ||
                                value.panel_type == "multiselect" ||
                                value.panel_type == "singleselect" ||
                                value.panel_type == "tabs"
                            ) || !value.id
                        )
                    },
                },
                {
                    name: "Header",
                    placeholder: "Header (optional)",
                    key: "header",
                    required: false,
                    maxlength: 250,
                    hidden(value) {
                        return value.panel_type === "chat_v2"
                    },
                },
                {
                    name: "Subheader",
                    key: "sub_header",
                    required: false,
                    type: "richtext",
                    rows: 3,
                    hidden(value) {
                        return ["custom", "chat", "chat_v2"].includes(value.panel_type) && !value.hasExistingSubheader
                    },
                },
                {
                    name: "Exclude this inquiry from score & summary",
                    type: "checkbox",
                    key: "exclude_from_total_score",
                    positionIndent: true,
                    condensedStyle: true,
                    description: `
                        When checked, this inquiry will be excluded from the learner's total score and learner response summary.
                    `,
                    hidden(value) {
                        return ![
                            "multichoice",
                            "multiselect",
                            "singleselect",
                            "textarea",
                            "step_order",
                            "chat",
                            "image_hotspots",
                            "categories",
                            "voice_recording",
                            "custom_form",
                        ].includes(value.panel_type)
                    },
                },
                {
                    name: "Only score the learner's first response",
                    key: "score_first_response_only",
                    type: "checkbox",
                    description:
                        "When checked, only the learner's first response/selection on this inquiry will count toward their overall score.",
                    positionIndent: true,
                    condensedStyle: true,
                    hidden(value) {
                        return !["multichoice", "multiselect", "singleselect", "image_hotspots"].includes(
                            value.panel_type
                        )
                    },
                },
                {
                    name: "Show a video first",
                    key: "show_video_first",
                    type: "checkbox",
                    description:
                        "Show a video before displaying this content. Enable fullscreen for an immersive experience.",
                    positionIndent: true,
                    condensedStyle: true,
                    hidden(value) {
                        return ![
                            "multichoice",
                            "multiselect",
                            "singleselect",
                            "step_order",
                            "tabs",
                            "textarea",
                            "custom",
                            "voice_recording",
                            "notepad",
                            "custom_form",
                            "categories",
                        ].includes(value.panel_type)
                    },
                },
            ]
        },
        simplePanelFormFields() {
            return [
                {
                    name: "Header",
                    key: "header",
                    required: false,
                },
            ]
        },
        imageHotspotsFormFields() {
            return [
                {
                    name: "",
                    type: "radio",
                    key: "hotspots_content_type",
                    options: [
                        {
                            value: "2d_image",
                            label: "2D Image",
                        },
                        {
                            value: "360_image",
                            label: "360° Image",
                        },
                    ],
                    inlineStyle: true,
                },
                {
                    name: "Discovery activity mode",
                    type: "checkbox",
                    key: "indicate_selected_choices",
                    positionIndent: false,
                    subtext:
                        "Show the learner how many hotspots they've found, and fade out hotspots they've explored.",
                    condensedStyle: true,
                },
                {
                    key: "choice_count_readout_mode",
                    type: "radio",
                    options: [
                        {
                            value: "all",
                            label: "Show a count of all hotspots discovered",
                        },
                        {
                            value: "correct_only",
                            label: "Only count correct hotspots",
                        },
                    ],
                    isSubOption: true,
                    hidden(value) {
                        return !value.indicate_selected_choices
                    },
                },
                {
                    name: "Display full screen",
                    type: "checkbox",
                    key: "display_full_screen",
                    positionIndent: false,
                    description: "Display the hotspots activity in fullscreen.",
                    subtext:
                        "When enabled, the header and subheader will be displayed in the bottom right hand corner of the screen.",
                },
                {
                    name: "Enable tabbing to hotspots",
                    type: "checkbox",
                    key: "enable_hotspot_tabbing",
                    positionIndent: false,
                    description:
                        "When checked, learners will be able to use the tab button on their keyboards to select hotspots.",
                },
                {
                    name: "360° Viewport Height",
                    type: "number",
                    key: "height",
                    placeholder: "600",
                    unit: "pixels",
                    required: false,
                    hidden(value) {
                        return value.hotspots_content_type == "2d_image"
                    },
                    disabled(value) {
                        return value.display_full_screen
                    },
                    displayInline: true,
                },
            ]
        },
        threeDVideoFormFields() {
            return [
                {
                    placeholder: "Screen reader scene description",
                    key: "accessibility_description",
                    subtext: "An alternative description of the 360° scene for learners using screen readers.",
                    type: "textarea",
                    rows: 2,
                    maxlength: 10000,
                },
                {
                    name: "Video height",
                    key: "height",
                    type: "number",
                    placeholder: "600",
                    unit: "pixels",
                    displayInline: true,
                    disabled(value) {
                        return value.display_full_screen
                    },
                },
                {
                    name: "Display full screen",
                    type: "checkbox",
                    key: "display_full_screen",
                    positionIndent: false,
                    subtext:
                        "When enabled, the header and subheader will be displayed in the bottom right hand corner of the screen.",
                },
            ]
        },
        textResponseFormFields() {
            let vm = this
            return [
                {
                    name: "Response required",
                    key: "textarea_required",
                    type: "checkbox",
                    positionIndent: true,
                    condensedStyle: true,
                },
                {
                    name: "Allow learners to change their response when revisiting this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    description:
                        "When checked, learners will be allowed to change their responses to this inquiry when revisiting this screen.",
                    positionIndent: true,
                    condensedStyle: true,
                },
                {
                    name: "Disable input after learner presses submit",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    description:
                        "Check this box to prevent learners from editing their response after they press submit.",
                    hidden(item) {
                        return !item.analyze_text_responses && !item.enable_ai_analysis
                    },
                    positionIndent: true,
                    condensedStyle: true,
                },
                {
                    name: "Continue from previous text response inquiry",
                    key: "textarea_continue_from_prev",
                    type: "checkbox",
                    positionIndent: true,
                    condensedStyle: this.panel.textarea_continue_from_prev,
                },
                {
                    key: "linked_response_panel",
                    required(item) {
                        return item.textarea_continue_from_prev
                    },
                    type: "search",
                    options: vm.textResponsePanelsFlat,
                    labelField: "title",
                    valueField: "id",
                    searchFields: ["title"],
                    maxItems: 1,
                    placeholder: "Select an inquiry to continue from...",
                    hidden(item) {
                        return !item.textarea_continue_from_prev
                    },
                    positionIndent: true,
                },
                {
                    name: "Text Box Placeholder",
                    key: "textarea_placeholder",
                    placeholder: "A placeholder message shown to learners before they enter their response",
                    type: "textarea",
                    rows: 2,
                    displayInline: true,
                },
                {
                    name: "Number of Rows Displayed",
                    key: "textarea_rows",
                    placeholder: "Auto",
                    type: "number",
                    displayInline: true,
                    positionIndent: true,
                },
                {
                    name: "Maximum Characters",
                    description: "Leave this field blank if you don't want learner response length to be limited.",
                    key: "character_limit",
                    placeholder: "No limit",
                    type: "number",
                    displayInline: true,
                },
                // Legacy feature - only shown if the panel already has this setting turned on to
                // support legacy functionality.
                {
                    name: "Analyze response for keywords",
                    key: "analyze_text_responses",
                    type: "checkbox",
                    condensedStyle: true,
                    badge: "Legacy",
                    hidden() {
                        return !vm.savedPanel.analyze_text_responses
                    },
                },
            ]
        },
        selectionPanelFormFields() {
            return [
                {
                    name: "Reveal correctness of choices on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    positionIndent: true,
                },
                {
                    name: "Allow learners to deselect choices after making them",
                    key: "allow_deselecting_multi",
                    type: "checkbox",
                    subtext: "Turn this setting off to make selections of choices permanent.",
                    positionIndent: true,
                    hidden(item) {
                        return item.panel_type !== "multiselect"
                    },
                },
                {
                    name: "Allow learners to change their selections when revisiting this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    description:
                        "When checked, learners will be allowed to change their responses to this inquiry when revisiting this screen.",
                    positionIndent: true,
                },
                {
                    name: "Record learner's selections instantly",
                    key: "record_selections_instantly",
                    type: "checkbox",
                    subtext: "Turn this setting off if you want choices to be submitted as a complete set.",
                    positionIndent: true,
                },
                {
                    name: "Show feedback before continuing",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    positionIndent: true,
                    hidden(item) {
                        return item.record_selections_instantly
                    },
                },
            ]
        },
        categoriesPanelFormFields() {
            return [
                {
                    name: "Dropzone size",
                    key: "dropzone_size",
                    type: "select",
                    description: "Adjust the size of the dropzones that items can be dragged into.",
                    options: [
                        {
                            label: "Small",
                            value: "small",
                        },
                        {
                            label: "Medium",
                            value: "medium",
                        },
                        {
                            label: "Large",
                            value: "large",
                        },
                        {
                            label: "Fill Containing Block",
                            value: "fill",
                        },
                    ],
                    autoWidth: true,
                },
            ]
        },
        categoriesPanelFormFieldsExtra() {
            return [
                {
                    name: "Freeze items after learner submits a response",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    subtext: "Check this box to prevent learners from moving items after they submit a response.",
                    positionIndent: true,
                },
                {
                    name: "Reveal correctness of placed items on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    description: "Control whether learners see the correctness of their item placements.",
                    positionIndent: true,
                },
                {
                    name: "Show feedback before continuing",
                    description: "Show feedback and correctness of item placements before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    positionIndent: true,
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        customFormPanelFields() {
            return [
                {
                    name: "Disable input fields after learner submits response",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    description:
                        "Check this box to prevent learners from editing their responses after they submit a response.",
                    positionIndent: true,
                },
                {
                    name: "Require that all fields are filled out",
                    key: "all_items_required",
                    type: "checkbox",
                    description: "Check this box to force learners to enter a value into each field in the form.",
                    positionIndent: true,
                },
            ]
        },
        customFormPanelFieldsExtra() {
            return [
                {
                    name: "Allow learners to change their response when revisiting this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    description:
                        "When checked, learners will be allowed to change their responses to this inquiry when revisiting this screen.",
                    positionIndent: true,
                },
                {
                    name: "Clear inputs when revisiting this screen",
                    description: "When checked, all form inputs will be set to blank when returning to this screen.",
                    key: "clear_inputs_when_returning",
                    type: "checkbox",
                    positionIndent: true,
                    hidden(value) {
                        return !value.allow_answer_changing
                    },
                },
                {
                    name: "Reveal correctness of fields on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    description: "Control whether learners see the correctness of their fields.",
                    positionIndent: true,
                },
                {
                    name: "Show feedback before continuing",
                    description: "Show feedback and correctness of the form before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    positionIndent: true,
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        stepOrderPanelFormFields() {
            return [
                {
                    name: "Freeze steps after learner presses submit",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    subtext: "Check this box to prevent learners from reordering their steps after they press submit.",
                    positionIndent: true,
                },
                {
                    name: "Reveal correctness of steps on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    description: "Control whether learners see the correctness of their steps after submitting.",
                    positionIndent: true,
                },
                {
                    name: "Show feedback before continuing",
                    description: "Show feedback and correctness of steps before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    positionIndent: true,
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        viewingChoicePanel() {
            if (!this.panel) {
                return false
            }
            const choicePanelTypes = ["multichoice", "singleselect", "multiselect"]
            if (choicePanelTypes.includes(this.panel.panel_type)) {
                return true
            }
            return false
        },
        viewingSelectionPanel() {
            if (!this.panel) {
                return false
            }
            const choicePanelTypes = ["singleselect", "multiselect"]
            if (choicePanelTypes.includes(this.panel.panel_type)) {
                return true
            }
            return false
        },
        viewingPanelWithExtraSettings() {
            return ["multiselect", "singleselect", "step_order", "custom_form", "categories"].includes(
                this.panel.panel_type
            )
        },
        vitalsSeriesCodesSerialized() {
            if (!this.panel.vitals) {
                return null
            }
            return JSON.stringify(this.panel.vitals.series.map((s) => s.code))
        },
        activeVitalsSeries() {
            if (!this.panel.vitals) {
                return []
            }
            return this.panel.vitals.series.filter((s) => !s.is_deleted)
        },
        panelImageNeedsUpload() {
            return (
                this.panel.elements &&
                this.panel.elements[0] &&
                this.panel.elements[0].stored_file &&
                !this.panel.elements[0].stored_file.id
            )
        },
        customFormEditorRoute() {
            if (!this.panel.id || !this.caseID || !this.nodeID) {
                return null
            }
            return {
                name: "CustomFormEditor",
                params: {
                    panelID: this.panel.id,
                    nodeID: this.nodeID,
                    caseID: this.caseID,
                },
            }
        },
        imageHotspotsWarningBytes() {
            return this.panel.hotspots_content_type == "2d_image" ? 3000000 : 25000000
        },
        imageHotspotsFileLabel() {
            return this.panel.hotspots_content_type == "2d_image" ? "2D image" : "360° image"
        },
        panelChoiceInquiries() {
            let choiceInquiries = this.panelsFlat
                .filter((p) => ["multiselect", "singleselect", "image_hotspots"].includes(p.panel_type))
                .map((p) => p.choices)
                .flat()
            return choiceInquiries
        },
        clonePanelFields() {
            let vm = this
            return [
                {
                    name: "Screen:",
                    key: "nodeId",
                    required: true,
                    type: "search",
                    options: vm.cloneNodeOptions,
                    loading: vm.cloneNodeOptionsLoading,
                    placeholder: "Select a screen to clone content into",
                    labelField: "label",
                    valueField: "id",
                    searchFields: ["label"],
                    maxItems: 1,
                },
            ]
        },
        clonePanelFormValid() {
            return this.clonePanelInsideNode || this.cloneForm.nodeId
        },
        voiceRecordingPanelFields() {
            return [
                {
                    name: "Max Length",
                    key: "recording_time_limit_sec",
                    description: "The maximum length of the learner's recording.",
                    type: "select",
                    options: [
                        {
                            label: "30 Seconds",
                            value: 30,
                        },
                        {
                            label: "1 Minute",
                            value: 60,
                        },
                        {
                            label: "2 Minutes",
                            value: 120,
                        },
                        {
                            label: "3 Minutes",
                            value: 180,
                        },
                        {
                            label: "4 Minutes",
                            value: 240,
                        },
                    ],
                    required: true,
                },
                {
                    type: "spacer",
                },
            ]
        },
        customGPTFormFields() {
            return [
                {
                    name: "Mood/Emotion",
                    key: "emotion",
                    type: "select",
                    options: [
                        {
                            value: "Neutral, succinct",
                            label: "Neutral, succinct",
                        },
                        {
                            value: "Irritable, angry",
                            label: "Irritable, angry",
                        },
                        {
                            value: "Confused, disoriented",
                            label: "Confused, disoriented",
                        },
                        {
                            value: "Cold, professional",
                            label: "Cold, professional",
                        },
                        {
                            value: "Impatient, annoyed",
                            label: "Impatient, annoyed",
                        },
                        {
                            value: "custom",
                            label: "Custom",
                        },
                    ],
                },
                {
                    name: " ",
                    type: "textarea",
                    key: "custom_emotion",
                    placeholder: "Describe the emotion the GPT should take on...",
                    rows: 2,
                    hidden(value) {
                        return value.emotion !== "custom"
                    },
                },
                {
                    name: "Respond in both voice and text",
                    subtext: "Note: When voice is enabled, the GPT will take slightly longer to respond.",
                    key: "respond_with_voice",
                    type: "switch",
                    positionIndent: true,
                },
                {
                    name: "Voice",
                    key: "voice_name",
                    type: "select",
                    hidden(value) {
                        return !value.respond_with_voice
                    },
                    options: [
                        {
                            value: "alloy",
                            label: "Alloy (neutral)",
                        },
                        {
                            value: "shimmer",
                            label: "Shimmer (neutral)",
                        },
                        {
                            value: "echo",
                            label: "Echo (low)",
                        },
                        {
                            value: "onyx",
                            label: "Onyx (lowest)",
                        },
                        {
                            value: "nova",
                            label: "Nova (high)",
                        },
                    ],
                },
            ]
        },
        customGPTFormExtraFields() {
            return [
                {
                    name: "Allow learners to continue chatting when returning to this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    positionIndent: true,
                },
                {
                    name: "Exclude this inquiry from score & summary",
                    type: "checkbox",
                    key: "exclude_from_total_score",
                    positionIndent: true,
                    condensedStyle: true,
                    description: `
                        When checked, this inquiry will be excluded from the learner's total score and learner response summary.
                    `,
                },
            ]
        },
    },
    watch: {
        cloneCaseId: function (newVal) {
            this.cloneForm.nodeId = null
            if (newVal) {
                this.fetchCloneNodeOptions()
            }
        },
        panel: {
            deep: true,
            // Track whether the panel config has been edited by the user.
            handler: function (newVal, oldVal) {
                if (newVal.id !== oldVal.id) {
                    this.panelIsEdited = false
                    return
                }
                this.panelIsEdited = true
            },
        },
    },
    methods: {
        open(panel) {
            this.openEditPanelModal(panel)
            this.fetchPanel(panel.id)
        },
        openDelete(panel) {
            this.openDeletePanel()
            this.fetchPanel(panel.id)
        },
        openEditPanelModal(panel) {
            this.toggleExtraSettingsDisplay(false)
            if (panel.panel_type === "custom") {
                $("#edit-custom-panel-modal").modal("show")
            } else if (panel.panel_type === "textarea") {
                this.fetchTextResponsePanels()
                $("#edit-textarea-panel-modal").modal("show")
            } else if (panel.panel_type === "vitals") {
                $("#edit-vitals-panel-modal").modal("show")
            } else if (
                ["multichoice", "multiselect", "singleselect", "step_order", "categories", "tabs"].includes(
                    panel.panel_type
                )
            ) {
                $("#edit-multichoice-panel-modal").modal("show")
            } else if (panel.panel_type === "linked_response") {
                $("#edit-linked-response-panel-modal").modal("show")
                this.fetchLinkablePanels()
            } else if (panel.panel_type === "chat") {
                this.$refs.chatPanelEditor.open()
            } else if (panel.panel_type === "discovery") {
                $("#discovery-panel-modal").modal("show")
            } else if (panel.panel_type === "score") {
                $("#edit-results-panel-modal").modal("show")
            } else if (panel.panel_type === "3d_image" || panel.panel_type === "3d_video") {
                $("#edit-vr-panel-modal").modal("show")
            } else if (panel.panel_type === "image_hotspots") {
                $("#edit-hotspots-panel-modal").modal("show")
            } else if (panel.panel_type === "custom_form") {
                $("#edit-custom-form-panel-modal").modal("show")
            } else if (panel.panel_type === "notepad") {
                $("#edit-notepad-panel-modal").modal("show")
            } else if (panel.panel_type === "voice_recording") {
                $("#edit-voice-panel-modal").modal("show")
            } else if (panel.panel_type === "chat_v2") {
                $("#edit-chat-v2-modal").modal("show")
            } else {
                $("#edit-basic-panel-modal").modal("show")
            }
        },
        openDeletePanel() {
            this.closeEditModals()
            $("#delete-panel-modal").modal("show")
        },
        cancelDeletePanel() {
            $("#delete-panel-modal").modal("hide")
        },
        // Create a choice on a panel.
        createChoice(index) {
            let action = this.panel.panel_type === "multichoice" ? "show_content_modal" : "show_text"
            let choice = {
                text: "",
                action: action,
                message: "",
                target_node: null,
                correct: false,
            }
            this.panel.choices.splice(index, 0, choice)
        },
        // Fetch a panel by ID for editing.
        fetchPanel(id, clearPanel = true) {
            this.modalLoading = true
            if (clearPanel) {
                // Reset the panel during loading.
                this.panel = {
                    vitals: null,
                    elements: [],
                    ai_analysis_config: {},
                }
            }
            let vm = this
            return http.get("/api/panels/" + id + "/").then(function (response) {
                vm.panel = response.data
                vm.savedPanel = Object.assign({}, response.data)
                vm.modalLoading = false

                // Separate out pre-content video elements from all other elements, so they can be
                // edited separately.
                vm.$set(
                    vm.panel,
                    "preContentVideoElements",
                    vm.panel.elements.filter((e) => e.is_pre_content_video)
                )
                vm.panel.elements = vm.panel.elements.filter((e) => !e.is_pre_content_video)

                if (vm.panel.panel_type === "vitals") {
                    vm.panel.vitals = vm.panel.vitals || {}
                } else if (vm.panel.panel_type == "custom" && vm.panel.sub_header) {
                    vm.panel.hasExistingSubheader = true
                }
                vm.panelIsEdited = false
            })
        },
        saveHotspotsPanel(showNotification) {
            // Check form validity.
            if (!this.$refs.imageHotspotsForm.validate()) {
                return false
            }
            let vm = this
            return new Promise((resolve) => {
                vm.savePanel(null, showNotification).then(() => {
                    resolve(true)
                })
            })
        },
        async saveChoicePanel() {
            await this.savePanel()
            this.$store.dispatch("addLoad")
            let vm = this
            this.$store.dispatch("refreshCase", this.caseID).finally(() => {
                vm.$store.dispatch("removeLoad")
            })
        },
        // Update an existing panel.
        savePanel(panelObj = null, showNotification = true) {
            let vm = this
            let panel = panelObj ? panelObj : this.panel

            // Special case - validate text response AI analysis form fields.
            if (
                panel.enable_ai_analysis &&
                !panel.ai_analysis_config.expert_response &&
                !panel.ai_analysis_config.extra_instructions &&
                !panel.ai_analysis_config.scoring_rubric
            ) {
                panel.enable_ai_analysis = false
            }

            this.modalLoading = true

            // Clone the panel object.
            panel = JSON.parse(JSON.stringify(panel))

            // Add back pre-content video elements to the list (which were separated upon load, for editing).
            panel.preContentVideoElements = panel.preContentVideoElements.map((e) => {
                return { ...e, is_pre_content_video: true }
            })
            panel.elements = panel.elements.concat(panel.preContentVideoElements)
            panel.elements.forEach((el, index) => {
                // Remove all signed URLs from elements - don't need to include these in the payload.
                if (el.stored_file) {
                    delete panel.elements[index].stored_file.signed_url
                }
                if (el.height === "") {
                    el.height = null
                }
            })

            // Remove blank elements.
            panel.elements = Utilities.filterBlankElements(panel.elements)
            panel.choices.forEach((choice, index) => {
                panel.choices[index].elements = Utilities.filterBlankElements(choice.elements)
            })

            // Set any blank scores to zero.
            panel.choices.filter((choice) => !choice.score).forEach((choice) => (choice.score = 0))
            panel.chat_intents.filter((intent) => !intent.score).forEach((intent) => (intent.score = 0))
            // Set blank panel height to zero.
            if (panel.height === "") {
                panel.height = null
            }
            // Remove any existing blank intents.
            if (panel.panel_type === "chat") {
                panel.chat_intents = panel.chat_intents.filter((intent) => Boolean(intent.code))
            } else if (panel.panel_type === "chat_v2") {
                panel.chat_intents = panel.chat_intents.filter((intent) => intent.ai_description || intent.label)
            }

            // Remove blank fields.
            if (panel.linked_response_panel == "") {
                panel.linked_response_panel = null
            }
            if (!panel.textarea_rows) {
                panel.textarea_rows = null
            }
            if (!panel.character_limit) {
                panel.character_limit = null
            }
            if (!panel.max_ai_score) {
                panel.max_ai_score = 0
            }

            return http
                .put("/api/panels/" + panel.id + "/", panel)
                .then(function () {
                    vm.modalLoading = false
                    vm.closeEditModals()
                    if (showNotification) {
                        vm.$notify({
                            group: "main",
                            type: "success",
                            text: "Content updated.",
                            data: {},
                        })
                    }
                    vm.$emit("updated", panel.id)
                    if (!vm.preserveMap) {
                        vm.$store.commit("mapRequiresUpdate")
                    }
                })
                .finally(() => (vm.modalLoading = false))
        },
        // Save an existing vitals panel.
        saveVitalsPanel() {
            this.modalLoading = true
            let formData = new FormData()

            // Clone panel object.
            let panelJson = JSON.stringify(this.panel)
            formData.append("panel", panelJson)

            if (this.panel.vitals.csv_file && this.panel.vitals.csv_file.file) {
                formData.append("csv", this.panel.vitals.csv_file.file)
            }

            let vm = this
            http.put("/api/panels/" + this.panel.id + "/vitals/", formData)
                .then(function () {
                    vm.modalLoading = false
                    $("#edit-vitals-panel-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content updated.",
                        data: {},
                    })
                    vm.$emit("updated", vm.panel.id)
                })
                .catch(function () {
                    vm.modalLoading = false
                })
        },
        // Delete an existing panel.
        deletePanel() {
            this.modalLoading = true
            let vm = this
            http.put("/api/panels/" + this.panel.id + "/remove/")
                .then(function () {
                    vm.modalLoading = false
                    $("#delete-panel-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content deleted.",
                        data: {},
                    })
                    vm.$emit("deleted", vm.panel.id)
                    vm.$emit("updated", vm.panel.id)
                    if (!vm.preserveMap) {
                        vm.$store.commit("mapRequiresUpdate")
                    }
                })
                .catch(function () {
                    vm.modalLoading = false
                })
        },
        // Attach a vitals csv file to the panel object.
        attachVitalsCSV(csvFile) {
            this.panel.vitals = {
                csv_file: csvFile,
            }
        },
        closeEditModals() {
            $("#edit-custom-panel-modal").modal("hide")
            $("#edit-multichoice-panel-modal").modal("hide")
            $("#edit-textarea-panel-modal").modal("hide")
            $("#edit-vitals-panel-modal").modal("hide")
            $("#edit-linked-response-panel-modal").modal("hide")
            $("#edit-basic-panel-modal").modal("hide")
            $("#edit-results-panel-modal").modal("hide")
            $("#edit-vr-panel-modal").modal("hide")
            $("#edit-hotspots-panel-modal").modal("hide")
            $("#edit-notepad-panel-modal").modal("hide")
            $("#edit-voice-panel-modal").modal("hide")
            $("#edit-custom-form-panel-modal").modal("hide")
            $("#discovery-panel-modal").modal("hide")
            $("#edit-chat-v2-modal").modal("hide")
            this.$refs.chatPanelEditor.close()
        },
        panelTypeIcon(panelType) {
            return panelTypeIcons[panelType]
        },
        panelTypeLabel(panelType) {
            let label = ""
            try {
                label = panelTypeLabels[panelType]
            } catch (error) {
                label = "Content"
            }
            return label
        },
        createVitalsSeries() {
            this.panel.vitals.series.push({
                code: "",
                hex_color: this.vitalsColorOptions[0],
                triggers: [],
            })
        },
        setVitalsColor(series, hexColor) {
            series.hex_color = hexColor
        },
        shiftVitalsSeriesPosition(index, increment) {
            // Don't do anything if series can't be moved further up or down.
            if ((index == 0 && increment == -1) || (index + 1 >= this.panel.vitals.series.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let series = this.panel.vitals.series.splice(index, 1)[0]
            // Put it back at the new position.
            this.panel.vitals.series.splice(index + increment, 0, series)
        },
        removeVitalsSeries(series) {
            this.$set(series, "is_deleted", true)
        },
        addTrigger(series) {
            series.triggers.push({
                low_boundary: null,
                high_boundary: null,
                change_type: "replace_with",
                trigger_type: "choice",
            })
        },
        removeTrigger(series, index) {
            series.triggers.splice(index, 1)
        },
        createTextEntity(panel) {
            panel.text_entities.push({
                correct: true,
                score: 0,
                display_label: "",
                keywords: [{ value: "" }],
            })
        },
        createKeywordVariation(textEntity) {
            textEntity.keywords.push({ value: "" })
        },
        removeTextEntity(panel, index) {
            panel.text_entities.splice(index, 1)
        },
        removeTextEntityKeyword(textEntity, index) {
            textEntity.keywords.splice(index, 1)
        },
        // Shift an item in a list by an increment.
        shiftListItem(index, increment, list) {
            // Don't do anything if the item is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= list.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let item = list.splice(index, 1)[0]
            // Put it back at the new position.
            list.splice(index + increment, 0, item)
        },
        // Remove an item from a list by its index.
        removeListItemByIndex(list, index) {
            list.splice(index, 1)
        },
        // Create a choice category on the currently selected panel.
        createChoiceCategory() {
            this.panel.choice_categories.push({
                name: "",
                tempId: String(Math.floor(Math.random() * 10000000)),
            })
        },
        savePanelOrder(panelIds) {
            http.put("/api/nodes/" + this.node.id + "/panels/order/", {
                panelIds: panelIds,
            })
        },
        // Fetch all text response panels for the case - used for linked response panels.
        fetchTextResponsePanels() {
            let vm = this
            http.get("/api/cases/" + this.caseID + "/panels/", {
                params: {
                    panelType: "textarea",
                },
            }).then(function (response) {
                vm.textResponsePanels = response.data
            })
        },
        fetchLinkablePanels() {
            let allowedPanelTypes = [
                "textarea",
                "voice_recording",
                "multiselect",
                "singleselect",
                "step_order",
                "categories",
                "custom_form",
                "chat",
                "chat_v2",
            ]
            let vm = this
            http.get("/api/cases/" + this.caseID + "/panels/", {
                params: {
                    panelType: allowedPanelTypes.join(","),
                },
            }).then(function (response) {
                vm.linkablePanels = response.data
            })
        },
        async openClone(panel = null) {
            if (panel) {
                await this.fetchPanel(panel.id)
            }
            this.$refs.clonePanelForm.resetValidate()
            this.closeEditModals()
            // If edit is locked, default to cloning outside the node so the user
            // is prompted to select a library item.
            if (this.editLocked) {
                this.clonePanelInsideNode = false
            }
            this.fetchCloneNodeOptions()
            // Fetch an initial set of items.
            this.$refs.itemSelector.searchDebounced("")
            $("#clone-panel-modal").modal("show")
            // If no library item is selected, select the library item currently being viewed.
            if (this.cloneForm.libraryItem === null && !this.editLocked) {
                // This is a hack to force the current library item to be selected in the library item
                // selector. We need to fetch the full representation of the associated case in order to
                // display the cover image.
                await this.$store.dispatch("fetchCase", this.caseID)
                let caseObj = this.$store.getters.case
                this.$refs.itemSelector.forceSelectItem({
                    item_type: "owned_case",
                    title: caseObj.public_title,
                    case: caseObj,
                })
            }
        },
        fetchCloneNodeOptions() {
            if (!this.cloneCaseId) {
                return
            }
            let vm = this
            this.cloneNodeOptionsLoading = true
            http.get(`/api/cases/${this.cloneCaseId}/nodes/`)
                .then((response) => {
                    vm.cloneNodeOptions = response.data
                })
                .finally(() => {
                    vm.cloneNodeOptionsLoading = false
                })
        },
        clonePanel() {
            // Check form validity.
            if (!this.clonePanelFormValid) {
                return
            }
            let nodeId
            if (this.clonePanelInsideNode) {
                nodeId = null
            } else {
                nodeId = this.cloneForm.nodeId
            }
            this.modalLoading = true
            let vm = this
            http.put(`/api/panels/${this.panel.id}/clone/`, {
                targetNodeID: nodeId,
            })
                .then(() => {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content cloned.",
                        data: {},
                    })
                    vm.$emit("updated")
                    $("#clone-panel-modal").modal("hide")
                })
                .finally(() => (vm.modalLoading = false))
        },
        toggleExtraSettingsDisplay(forceDisplayState = null) {
            if (this.allChoiceSettingsDisplayed || forceDisplayState === false) {
                $(this.$refs.extraChoiceFields).collapse("hide")
                $(this.$refs.extraStepFields).collapse("hide")
                $(this.$refs.extraCategoriesFields).collapse("hide")
                $(this.$refs.extraFormFields).collapse("hide")
                this.allChoiceSettingsDisplayed = false
            } else {
                $(this.$refs.extraChoiceFields).collapse("show")
                $(this.$refs.extraStepFields).collapse("show")
                $(this.$refs.extraCategoriesFields).collapse("show")
                $(this.$refs.extraFormFields).collapse("show")
                this.allChoiceSettingsDisplayed = true
            }
        },
        async navToHotspotsEditor() {
            if (!this.panel.id || !this.caseID || !this.nodeID) {
                return
            }
            if (!this.editLocked && this.panelIsEdited) {
                // Save the panel before navigating to the editor.
                let panelSaved = await this.saveHotspotsPanel(false)
                if (!panelSaved) {
                    return
                }
            }
            let hotspotsEditorRoute = {
                name: "ImageHotspotsEditor",
                params: {
                    panelID: this.panel.id,
                    nodeID: this.nodeID,
                    caseID: this.caseID,
                },
            }
            this.$router.push(hotspotsEditorRoute)
        },
        // Add a new chat intent.
        createIntent(index) {
            let intent = {
                message: "",
                code: "",
                correct: true,
                detection_type: "intent",
                ai_description: "",
            }
            this.panel.chat_intents.splice(index, 0, intent)
            let vm = this
            this.$nextTick(() => {
                vm.$refs.intentMessage[this.panel.chat_intents.length - 1].focus()
            })
        },
        // Remove an intent (mark it as deleted).
        removeIntent(intent) {
            this.$set(intent, "is_deleted", true)
        },
        // Generate a random chat persona description.
        generateChatPersona(panel) {
            if (panel.isGeneratingPersona) {
                return
            }
            this.$set(panel, "isGeneratingPersona", true)
            let payload = {
                organizationId: this.currentOrganizationId,
            }
            let vm = this
            http.post("/api/generate-persona/", payload)
                .then((response) => {
                    panel.ai_analysis_config.persona_details = response.data.content
                })
                .catch((error) => {
                    vm.$set(panel, "errorMessage", error.response.data.message)
                })
                .finally(() => {
                    panel.isGeneratingPersona = false
                })
        },
        showIntentFeedbackField(intent, index) {
            this.$set(intent, "showFeedbackInput", true)
            // Bring the input into focus.
            this.$nextTick(() => this.$refs.intentFeedbackMessageInput[index].focus())
        },
    },
    mounted() {
        let vm = this
        $('[data-panel-edit-modal="true"]').on("shown.bs.modal", function () {
            vm.$store.commit("setEditPanelModalIsOpen", true)
        })
        $('[data-panel-edit-modal="true"]').on("hidden.bs.modal", function () {
            vm.$store.commit("setEditPanelModalIsOpen", false)
        })
    },
}

const customPanelFormFields = [
    {
        name: "Show horizontal dividers between elements",
        key: "display_hr_dividers",
        type: "checkbox",
        positionIndent: true,
        condensedStyle: true,
    },
]

const textEntityFormFields = [
    {
        name: "Display Label",
        key: "display_label",
        type: "text",
        description: "A label to identify this piece of information.",
    },
    {
        name: "Score",
        key: "score",
        type: "number",
        description: "This score will only be applied if this piece of information is matched.",
    },
    {
        name: "Correct/Incorrect",
        key: "correct",
        type: "select",
        options: [
            // Omitting a neutral option here for now - trying to simplify the number of config
            // options for this interaction.
            {
                label: "Correct (Should Be Included)",
                value: true,
            },
            {
                label: "Incorrect (Should Not Be Included)",
                value: false,
            },
        ],
    },
    {
        name: "Hint Message",
        key: "feedback_message",
        type: "textarea",
        rows: 2,
        description: "A feedback message to show the learner if they omit this information.",
    },
]

const branchingPanelFormFields = [
    {
        name: "Disable buttons after they're pressed",
        key: "disable_items_after_selection",
        type: "checkbox",
        description: "When checked, buttons will be disabled after they're pressed by the learner for the first time.",
        positionIndent: true,
    },
]

const scorePanelFormFields = [
    {
        name: "Display the learner's total score",
        key: "display_score",
        type: "checkbox",
        positionIndent: true,
    },
    {
        name: "Display a breakdown of the learner's results",
        key: "display_breakdown",
        type: "checkbox",
        positionIndent: true,
        description: `
            Display a report containing the learner's responses during their
            session, with the ability to download as a PDF.
        `,
    },
    {
        name: "Display pass/fail status",
        key: "display_pass_fail",
        type: "checkbox",
        positionIndent: true,
        description: `
            Display whether the learner was successful or unsuccessful during their session.
        `,
    },
    {
        name: "Include Notepad notes",
        key: "include_learner_notes",
        type: "checkbox",
        positionIndent: true,
        description: `
            When checked, the learner's entries in the Notepad content-type will be included in the results summary.
        `,
    },
]

const notepadPanelFormFields = [
    {
        name: "Notepad Placeholder",
        key: "textarea_placeholder",
        placeholder: 'e.g. "Your Notes Here"',
    },
    {
        name: "Notepad Rows",
        key: "textarea_rows",
        type: "number",
        placeholder: "The minimum number of rows to display for the notepad (optional)",
    },
]

const discoveryPanelFormFields = [
    {
        name: "Include neutral & incorrect items",
        subtext: "When checked, neutral and incorrect items will be displayed in the discovered items list.",
        key: "include_non_correct_items",
        type: "checkbox",
        positionIndent: true,
    },
]
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";

// Rich text editor.
.ProseMirror {
    height: auto;
    min-height: 100px;
}

.vitals-code {
    p {
        margin-bottom: 0px !important;
    }
}

.alt-text-input {
    max-width: 350px;
}
</style>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/nodemap.scss";
@import "@/assets/styles/paneledit.scss";

.vitals-color-dropdown {
    max-width: 6rem;
    min-width: 5rem !important;
}

.vitals-color-preview {
    height: 20px;
    width: 30px;
}

.vitals-boundary-input {
    max-width: 120px;
}

.vitals-glyph-spacer {
    width: 24px;
    text-align: center;
}

.vitals-boundary-code {
    width: 60px;
    white-space: nowrap;
    margin-right: 5px;
    overflow: hidden;
}

.category-name-input {
    max-width: 600px;
}

.icon-glyphicons-basic-squares {
    vertical-align: -1px;
}

.tabs-layout-container {
    padding-left: 1.3rem;
    padding-right: 1.3rem;
    padding-top: 1.15rem;
    padding-bottom: 0.8rem;
}
</style>